import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { snackbarConstants } from './SnackbarContainer';
import { Close } from '../Close';
import { Loader } from '../Loader';
import styles from '@dtsl/css-snackbar/dist/index.module.css';
import Error from '@dtsl/icons/dist/icons/react/Error';
import Information from '@dtsl/icons/dist/icons/react/Information';
import Success from '@dtsl/icons/dist/icons/react/Success';
import Warning from '@dtsl/icons/dist/icons/react/Warning';
function Snackbar(props) {
  const {
    type,
    message = '',
    loading = false,
    duration = 10000,
    onClose = () => {},
    removeNodeFromList,
    identifier = 1,
    showCloseButton = false,
    wordbreak = false
  } = props;
  const [showSnackbar, setShowSnackbar] = useState(false);
  const timerRef = useRef(null);
  const [progressBarAnimation, setProgressBarAnimation] = useState({
    animationPlayState: 'paused',
    opacity: 0
  });
  const snackbarMessageClasses = classNames({
    [styles['sib-snackbar__message']]: true,
    [styles['sib-snackbar__message--wordbreak']]: wordbreak
  });
  useEffect(() => {
    setShowSnackbar(true);
    if (!loading) {
      startSnackbarAnimation();
    }
    if (loading) {
      pauseSnackbarAnimation();
    }
    return () => clearTimeout(timerRef.current);
  }, [duration, loading]);
  const startSnackbarAnimation = () => {
    setProgressBarAnimation({
      ...setProgressBarAnimation,
      opacity: 1,
      animationPlayState: 'running'
    });
  };
  const pauseSnackbarAnimation = () => {
    setProgressBarAnimation({
      ...setProgressBarAnimation,
      animationPlayState: 'paused'
    });
  };
  const hideSnackbar = () => {
    setShowSnackbar(false);
    setProgressBarAnimation({
      ...setProgressBarAnimation,
      opacity: 0,
      animationPlayState: 'paused'
    });
    timerRef.current = setTimeout(() => {
      onClose(identifier, handleClose);
      removeNodeFromList(identifier);
    }, 200);
  };
  const handleClose = event => {
    event.preventDefault();
    event.stopPropagation();
    hideSnackbar();
    props.handleClose();
  };

  //  render snackbar icon type method
  const renderSnackbarTypeIcon = type => {
    switch (type) {
      case snackbarConstants.SUCCESS:
        return /*#__PURE__*/React.createElement(Success, {
          className: "sib-snackbar--success-svg"
        });
      case snackbarConstants.ERROR:
        return /*#__PURE__*/React.createElement(Error, {
          className: "sib-snackbar--error-svg"
        });
      case snackbarConstants.WARNING:
        return /*#__PURE__*/React.createElement(Warning, {
          className: "sib-snackbar--warning-svg"
        });
      default:
        return /*#__PURE__*/React.createElement(Information, {
          className: "sib-snackbar--info-svg"
        });
    }
  };
  if (loading) {
    return /*#__PURE__*/React.createElement("div", {
      className: `${styles['sib-snackbar']} ${showSnackbar ? styles['show-snackbar'] : styles['hide-snackbar']} ${styles['sib-snackbar--info']}`,
      key: identifier
    }, /*#__PURE__*/React.createElement("div", {
      role: "progressbar",
      "aria-hidden": "false",
      "aria-label": "notification timer",
      className: `${styles['sib-snackbar__progress-bar']} ${styles['snackbar__progress-bar--info']}`,
      style: {
        width: '100%'
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: styles['sib-snackbar__content']
    }, /*#__PURE__*/React.createElement(Loader, {
      isAbsolute: false
    }), /*#__PURE__*/React.createElement("div", {
      className: styles['sib-snackbar__message--loading']
    }, message)));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles['sib-snackbar']} ${showSnackbar ? styles['show-snackbar'] : styles['hide-snackbar']} ${styles[`snackbar--${type}`]}`,
    key: identifier,
    onMouseEnter: () => pauseSnackbarAnimation(),
    onMouseLeave: () => startSnackbarAnimation()
  }, /*#__PURE__*/React.createElement("div", {
    role: "progressbar",
    "aria-hidden": "false",
    "aria-label": "notification timer",
    className: `${styles['sib-snackbar__progress-bar']} ${styles['snackbar__progress-bar--animated']} ${styles[`snackbar__progress-bar--${type}`]}`,
    onAnimationEnd: hideSnackbar,
    style: {
      animationDuration: `${duration}ms`,
      animationPlayState: progressBarAnimation.animationPlayState,
      opacity: progressBarAnimation.opacity
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['sib-snackbar__content']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-snackbar__icon']
  }, renderSnackbarTypeIcon(type)), /*#__PURE__*/React.createElement("div", {
    className: snackbarMessageClasses
  }, message), showCloseButton && /*#__PURE__*/React.createElement(Close, {
    onClick: handleClose,
    className: styles['sib-snackbar__close-button']
  })));
}
export default /*#__PURE__*/React.memo(Snackbar);
Snackbar.propTypes = {
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  wordbreak: PropTypes.bool,
  duration: PropTypes.number,
  onClose: PropTypes.func,
  removeNodeFromList: PropTypes.func,
  handleClose: PropTypes.func,
  showCloseButton: PropTypes.bool,
  loading: PropTypes.bool,
  type: PropTypes.string.isRequired,
  identifier: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};